import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { posthog } from '../../lib/analytics/posthogClient';
import type { Event, TerritoryResolution } from '@buzzmap/shared';
import { useUserPreferences, useTopRankedEvents, useWeekendNearbyEvents } from '../../hooks';
import { useGalleryScope } from '../../hooks/useGalleryScope';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useDiscoveryNav } from '../../hooks/useDiscoveryNav';
import { useGeolocationContext } from '../../contexts/GeolocationContext';
import { useDiscoveryMode, getDiscoveryMode } from '../../contexts/discoveryStore';
import { haversineKm } from '../../utils/distance';
import { trackEvent } from '../../utils/analytics';
import { exposeSsrRoot, removeSsrShell, ssrShellHasEventLinks } from '../../utils/ssrShell';
import { categoryHref, parseCategorieParam } from '../../utils/categoryLinks';
import { requiresTerritoryDocumentNavigation } from '../../routes/territoryPaths';
import { illustrationImage, logoImage } from '../../utils/brandImages';
import { categoryLongLabel } from '../../utils/categories';
import { LocationPill } from '../filters/LocationPill';
import { LocalitaPopup } from '../filters/LocalitaPopup';
import { EventFeatCard } from './EventFeatCard';
import { TerritoryHeader } from '../territory/TerritoryHeader';
import { TerritoryShowcase } from '../territory/TerritoryShowcase';
import styles from './HomeDesktop.module.css';

// Dimensioni reali nel CSS: logo alto 88 px (88 * 483/170 = 250 px), icone dei tile 28 px.
const HERO_LOGO_SIZES = '250px';
const TILE_ICON_SIZES = '28px';

/**
 * T105-FE: la prima carta di "Imperdibili" è candidata LCP solo se ne è visibile una parte
 * rilevante senza scorrere. Misurato (context-task-T105.md): a 1280 px di larghezza la carta (alta
 * 350 px) inizia a y=811 e sotto una viewport alta 800 px resta fuori; con almeno il 40% della
 * carta visibile (811 + 140 = ~950 px) può contendersi l'LCP con logo e testi.
 * Assunzione: i 950 px sono misurati solo a 1280 px di larghezza; a 1024 px la carta inizia più
 * in basso (y=911), quindi lì la soglia sovrastima leggermente la visibilità.
 */
const FIRST_CARD_VISIBLE_QUERY = '(min-height: 950px)';

/**
 * Category tiles for the desktop "Esplora per categoria" grid.
 * One tile per category that has a page; 'altro' has none and is left out,
 * like the mobile chips (`categoryOptions`).
 *
 * Labels come from `categoryLongLabel` (tiles have room for the full name);
 * hrefs from `categoryHref`: a territory page links to the real category
 * pages under it, the plain homepage to /esplora?category={navId}. Each tile
 * is a `<a href>` because Google only follows anchors.
 */
const CATEGORY_TILES: ReadonlyArray<{
  /** Display id, also the `data-cat` hook for the per-category tile colors. */
  id: string;
  /** DB category id — the value that goes into links and analytics. */
  navId: string;
}> = [
  { id: 'mercatino', navId: 'mercatino' },
  { id: 'sagra', navId: 'sagra' },
  { id: 'fiera', navId: 'fiera' },
  { id: 'musica', navId: 'musica' },
  { id: 'bambini', navId: 'bambini' },
  { id: 'sport', navId: 'sport' },
  { id: 'cinema', navId: 'cinema_teatro' },
  { id: 'tradizione', navId: 'tradizione' },
  { id: 'festivita', navId: 'festivita' },
  { id: 'rievocazione', navId: 'rievocazione' },
  { id: 'arte', navId: 'arte' },
];

interface CarouselProps {
  events: Event[];
  loading: boolean;
  emptyLabel: string;
  onEventClick: (id: string) => void;
  /** La prima carta è il candidato LCP della vista iniziale (T105-FE). Default false. */
  prioritizeFirstCard?: boolean;
}

function Carousel({
  events,
  loading,
  emptyLabel,
  onEventClick,
  prioritizeFirstCard = false,
}: CarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const scroll = (dir: -1 | 1) => {
    trackRef.current?.scrollBy({ left: dir * 600, behavior: 'smooth' });
  };

  return (
    <div className={styles.carouselWrap}>
      <button
        type="button"
        className={`${styles.carouselArrow} ${styles.left}`}
        onClick={() => scroll(-1)}
        aria-label="Scorri indietro"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <div className={styles.carouselTrack} ref={trackRef}>
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={styles.skeletonCard} aria-hidden="true" />
          ))
        ) : events.length === 0 ? (
          <div className={styles.emptyState}>{emptyLabel}</div>
        ) : (
          events.map((ev, index) => (
            <EventFeatCard
              key={ev.id}
              event={ev}
              onClick={() => onEventClick(ev.id)}
              priority={prioritizeFirstCard && index === 0}
            />
          ))
        )}
      </div>
      <button
        type="button"
        className={`${styles.carouselArrow} ${styles.right}`}
        onClick={() => scroll(1)}
        aria-label="Scorri avanti"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
  );
}

interface HomeDesktopProps {
  resolution: TerritoryResolution | null;
  /** From `useTerritoryRouteResolution` in `HomePage`: true once the
   *  territory resolution attempt finished one way or another (T015-FE). */
  settled: boolean;
}

export function HomeDesktop({ resolution, settled }: HomeDesktopProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLocationChoice, effectiveLocation, locationResolutionPending } = useUserPreferences();
  const { requestLocation } = useGeolocationContext();
  const discoveryMode = useDiscoveryMode();
  // T013: hero/CTA/category navigation targets — the shared builder in v2
  // (territorial surfaces; the weekend CTA keeps its explicit dates), the
  // literals in legacy.
  const nav = useDiscoveryNav();

  // Spatial + filter scope for the galleries (T012-FE): v2 queries ALL
  // resolved search-area centers and obeys the explicit URL categories/dates;
  // legacy keeps the single-center/favorites behaviour (plan l.64).
  const galleryScope = useGalleryScope(resolution);
  // Un solo candidato high per vista: la prima carta di "Imperdibili", e solo se il viewport è
  // abbastanza alto da mostrarla senza scorrere. Il carosello del weekend è sempre lazy.
  const firstCardInView = useMediaQuery(FIRST_CARD_VISIBLE_QUERY);

  const topRanked = useTopRankedEvents({
    centers: galleryScope.centers,
    coords: galleryScope.coords,
    radiusKm: galleryScope.radiusKm,
    daysAhead: 30,
    limit: 15,
    categories: galleryScope.categories,
    startDate: galleryScope.startDate,
    endDate: galleryScope.endDate,
    skip: locationResolutionPending,
  });

  const weekend = useWeekendNearbyEvents({
    centers: galleryScope.centers,
    coords: galleryScope.coords,
    radiusKm: galleryScope.radiusKm,
    limit: 12,
    categories: galleryScope.weekendCategories,
    startDate: galleryScope.startDate,
    endDate: galleryScope.endDate,
    skip: locationResolutionPending,
  });

  // Questo ramo possiede il rilascio del guscio desktop. Come HomePage,
  // attende risoluzione pertinente e settled, galleria senza errori né
  // caricamento e contenuto reale nella galleria o nella vetrina resa;
  // un guscio senza link evento può essere vuoto. Rimozione in ssrShell.ts.
  useEffect(() => {
    if (!settled || resolution === null) return;
    if (topRanked.loading) return;
    // La vetrina resa è contenuto reale anche con il feed del periodo vuoto.
    if (
      !topRanked.error &&
      (topRanked.events.length > 0 ||
        (resolution.showcase?.length ?? 0) > 0 ||
        !ssrShellHasEventLinks())
    ) {
      removeSsrShell();
      return;
    }
    // Settled ma non pronto (feed in errore, o vuoto contro un guscio pieno):
    // il guscio resta, ma l'app sotto di esso deve restare usabile (T102-INT).
    exposeSsrRoot();
  }, [settled, resolution, topRanked.loading, topRanked.error, topRanked.events.length]);

  // Background refresh for GPS users (parity with mobile HomePage)
  // T011-FE: eliminated in v2 (no movement/background GPS refresh after the
  // gate opens); kept in `legacy` for pre-activation parity.
  useEffect(() => {
    if (discoveryMode === 'v2') return;
    if (effectiveLocation.type !== 'gps') return;
    const cachedCoords = effectiveLocation.coords;
    const cachedRadius = effectiveLocation.radiusKm;
    requestLocation({ silent: true }).then((result) => {
      if (!result.coords) return;
      // W1 (T011 review): re-check the LIVE mode — a fix acquired before the
      // gate opened must never apply a background placement in v2.
      if (getDiscoveryMode() === 'v2') return;
      const dist = haversineKm(result.coords, cachedCoords);
      if (dist > 5) {
        setLocationChoice({
          type: 'gps',
          coords: result.coords,
          radiusKm: cachedRadius,
          ts: Date.now(),
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLocation.type]);

  const [openPopup, setOpenPopup] = useState<'localita' | null>(null);
  const localitaPillRef = useRef<HTMLButtonElement>(null);
  // A `?categorie=` selection on the home IS an applied filter on the
  // galleries below — the tiles must show it (the default is "no category",
  // and then no tile is marked).
  const selectedCategories = parseCategorieParam(location.search);

  const handleEventClick = (id: string) => {
    // Desktop: open as modal over current page via backgroundLocation.
    navigate(`/event/${id}`, { state: { backgroundLocation: location } });
  };

  return (
    <main className={styles.page}>
      <section className={styles.hero} aria-label="Buzzmap">
        <img {...logoImage(HERO_LOGO_SIZES)} alt="Buzz Event Map" className={styles.heroLogo} />
        <p className={styles.heroSubtitle}>Scopri cosa succede intorno a te</p>
        <div className={styles.heroControls}>
          <LocationPill
            ref={localitaPillRef}
            variant="header"
            onClick={() => setOpenPopup('localita')}
          />
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => navigate(nav.esploraUrl)}
          >
            <span>Tutti gli eventi</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      </section>

      <section className={styles.content}>
        {/* Category grid */}
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Esplora per categoria</h2>
            <p className={styles.sectionSubtitle}>
              11 modi per trovare l&apos;evento giusto per te
            </p>
          </div>
        </div>

        <div className={styles.catGrid}>
          {CATEGORY_TILES.map((cat) => (
            <Link
              key={cat.id}
              // T013: v2 builds the tile through the shared patch builder from
              // the current URL; legacy keeps `categoryHref`. `reloadDocument`
              // is upstream's period hand-off (see HomePage) and evaluates the
              // SAME target the link uses.
              to={nav.categoryUrl(cat.navId) ?? categoryHref(cat.navId, resolution, location)}
              reloadDocument={requiresTerritoryDocumentNavigation(
                location.pathname,
                location.search,
                nav.categoryUrl(cat.navId) ?? categoryHref(cat.navId, resolution, location)
              )}
              className={styles.catTile}
              data-cat={cat.id}
              aria-current={selectedCategories.includes(cat.navId) ? 'true' : undefined}
              onClick={() => {
                posthog.capture('category_selected', {
                  category: cat.navId,
                  label: categoryLongLabel(cat.navId),
                  source: 'home_desktop',
                });
              }}
            >
              <span className={styles.catTileIcon}>
                <img {...illustrationImage(cat.navId, TILE_ICON_SIZES)} alt="" />
              </span>
              <span className={styles.catTileBody}>
                <span className={styles.catTileLabel}>{categoryLongLabel(cat.navId)}</span>
              </span>
            </Link>
          ))}
        </div>

        {/* Featured carousel, opened by the territory heading (mobile parity) */}
        <TerritoryHeader resolution={resolution} hideCategoryList variant="plain-light" />
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Imperdibili</h2>
            <p className={styles.sectionSubtitle}>Le selezioni piu curate della settimana</p>
          </div>
          <button
            type="button"
            className={styles.linkAll}
            onClick={() => navigate(nav.esploraUrl)}
          >
            Vedi tutti →
          </button>
        </div>
        <Carousel
          events={topRanked.events}
          loading={topRanked.loading}
          emptyLabel="Nessun evento da mostrare al momento."
          onEventClick={handleEventClick}
          prioritizeFirstCard={firstCardInView}
        />

        <TerritoryShowcase items={resolution?.showcase ?? []} />

        {/* Weekend carousel */}
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Questo weekend</h2>
            <p className={styles.sectionSubtitle}>Gli eventi del prossimo sabato e domenica</p>
          </div>
          <button
            type="button"
            className={styles.linkAll}
            // T013: the CTA preserves its explicit dates; in v2 it lands on
            // the territorial Esplora surface of the current URL.
            onClick={() => navigate(nav.weekendUrl(weekend.weekendStart, weekend.weekendEnd))}
          >
            Vedi tutti →
          </button>
        </div>
        <Carousel
          events={weekend.events}
          loading={weekend.loading}
          emptyLabel="Nessun evento del weekend nella tua zona."
          onEventClick={handleEventClick}
        />
      </section>

      <LocalitaPopup
        open={openPopup === 'localita'}
        onClose={() => setOpenPopup(null)}
        anchorEl={localitaPillRef.current}
        onChange={() => {
          trackEvent('filter_apply', { type: 'localita' });
        }}
      />
    </main>
  );
}
